"use client";

import { memo, forwardRef } from "react";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha, styled } from "@mui/material/styles";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { flatListBorder, flatListSurfaceAlt } from "src/theme/core/flat-list-surface";

import type { CardProps, CardRadius, CardVariant, CardPadding } from "./types";

/**
 * Tokens centrais. Mudar a "cara" de todos os cards = editar aqui.
 *
 * `padding` é responsivo via theme.spacing — preserva o sistema atual
 * (xs:2, sm:2.5, md:3.5 no antigo `getDashboardCardChrome`).
 */
// Escala de radius em spacing units (×8px). `md` = 12px é o default e a "cara"
// padrão de card do produto — um pouco mais reto que o backoffice (16px), que
// lê arredondado demais em cards largos de lista (ex.: lista de partidas).
const RADIUS_TOKENS: Record<CardRadius, number> = {
  sm: 1, // 8px
  md: 1.5, // 12px — default
  lg: 2, // 16px
};

const PADDING_TOKENS: Record<CardPadding, { xs: number; sm: number; md: number }> = {
  none: { xs: 0, sm: 0, md: 0 },
  sm: { xs: 1.5, sm: 2, md: 2 },
  md: { xs: 2, sm: 2.5, md: 3.5 },
  lg: { xs: 2.5, sm: 3, md: 4 },
};

const DEFAULT_VARIANT: CardVariant = "filled";
const DEFAULT_PADDING: CardPadding = "md";
const DEFAULT_RADIUS: CardRadius = "md";

// Props customizadas com nomes não-conflitantes (Box já tem `padding`).
type StyledRootExtra = {
  $variant: CardVariant;
  $padding: CardPadding;
  $radius: CardRadius;
  $interactive: boolean;
};

/**
 * Base estilizada compartilhada entre o modo estático e o interativo.
 * Usa `styled()` para gerar classes estáticas — zero alocação de objetos
 * de estilo por render.
 *
 * Prefixo `$` nas props evita conflito com props do `Box` (padding etc.)
 * e impede vazamento pro DOM.
 */
const sharedRootStyles = styled(Box, {
  name: "DsCard",
  slot: "Root",
  shouldForwardProp: (prop) => typeof prop === "string" && !prop.startsWith("$"),
})<StyledRootExtra>(({ theme, $variant, $padding, $radius, $interactive }) => {
  const pad = PADDING_TOKENS[$padding];

  return {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    alignItems: "stretch",
    position: "relative",

    // Estética ÚNICA de card do produto, espelhando o card do
    // natrave-backoffice (`DashboardMetricCard` da Home): superfície sólida
    // `background.paper` (#121212, um degrau acima do fundo #0A0A0A), borda
    // `divider` (#262626), radius 16 e sombra sutil. Consumidores NÃO devem
    // sobrescrever bg/border/radius via `sx` — mudar a "cara" = editar aqui.
    backgroundColor:
      $variant === "ghost" || theme.palette.mode === "light"
        ? "transparent"
        : theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(RADIUS_TOKENS[$radius]),
    boxShadow:
      $variant === "ghost" || theme.palette.mode === "light"
        ? "none"
        : "0px 16px 40px -32px rgba(0,0,0,0.45)",

    padding: theme.spacing(pad.xs),
    [theme.breakpoints.up("sm")]: { padding: theme.spacing(pad.sm) },
    [theme.breakpoints.up("md")]: { padding: theme.spacing(pad.md) },

    transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
      duration: theme.transitions.duration.shorter,
    }),

    ...($interactive
      ? {
          cursor: "pointer",
          "&:hover": {
            borderColor:
              theme.palette.mode === "light" ? flatListBorder(theme) : theme.palette.primary.main,
            backgroundColor:
              theme.palette.mode === "light"
                ? flatListSurfaceAlt(theme)
                : theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "light"
                ? "none"
                : "0px 16px 48px -28px rgba(0,0,0,0.60)",
          },
          "&:focus-visible": {
            outline: "none",
            borderColor: alpha(theme.palette.text.primary, 0.45),
            boxShadow: `0 0 0 3px ${alpha(theme.palette.text.primary, 0.12)}`,
          },
          "&.Mui-disabled": {
            opacity: 0.6,
            cursor: "not-allowed",
          },
          "& .DsCard-arrow": {
            transition: theme.transitions.create(["transform", "color"], {
              duration: theme.transitions.duration.shorter,
            }),
          },
          "&:hover .DsCard-arrow": {
            transform: "translateX(3px)",
            color: theme.palette.text.primary,
          },
        }
      : {}),
  };
});

/**
 * Variante estática herda os mesmos estilos, renderizando como `<div>`
 * (via `Box`) — leve, sem ripple, sem foco implícito.
 */
const StaticRoot = sharedRootStyles;

/**
 * Variante interativa: substitui o componente raiz por `ButtonBase` para
 * obter semântica de botão (Enter/Space, role=button, focus correto)
 * sem reescrever os estilos.
 */
const InteractiveRoot = styled(sharedRootStyles, {
  name: "DsCard",
  slot: "InteractiveRoot",
})({}).withComponent(ButtonBase);

/**
 * Seta de affordance. Memoizada externamente — não tem nada que mude
 * entre renders.
 */
const Arrow = memo(function CardArrow() {
  return (
    <Box
      className="DsCard-arrow"
      aria-hidden
      sx={(theme) => ({
        position: "absolute",
        bottom: theme.spacing(2.25),
        right: theme.spacing(2.25),
        color: theme.palette.grey[500],
        display: "inline-flex",
        pointerEvents: "none",
      })}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
    </Box>
  );
});

/**
 * **Card** — casca padrão de cards da plataforma.
 *
 * Dois modos via discriminated union — o TypeScript escolhe a API:
 *
 *  - **Estático**: `<Card>...</Card>` renderiza `<div>`.
 *  - **Interativo**: `<Card interactive onClick={...} ariaLabel="...">`
 *    renderiza `<button>` com hover/focus/seta automáticos.
 *
 * Tokens controlam todo o visual:
 *
 *  - `variant`: `filled` (superfície `background.paper` #121212 + borda
 *    `divider` + sombra — a "cara" padrão de card, = natrave-backoffice) |
 *    `ghost` (transparente, sem borda/sombra)
 *  - `padding`: `none` | `sm` | `md` | `lg` (responsivo)
 *  - `radius`: `sm` 8 | `md` 12 (default) | `lg` 16
 *
 * Performance:
 *  - `styled()` em vez de `sx` cru → estilos cacheados.
 *  - `memo` + `forwardRef` → integra-se com listas virtualizadas e refs externas.
 *  - Seta isolada e memoizada → não re-renderiza com o conteúdo.
 *
 * Acessibilidade:
 *  - Modo interativo exige `ariaLabel` em compile-time.
 *  - `:focus-visible` cria anel apenas para navegação por teclado.
 *  - Conteúdo decorativo (a seta) marcado com `aria-hidden`.
 *
 * @example Estático
 * ```tsx
 * <Card padding="md">
 *   <CardHeader title="Times inscritos" />
 *   <TeamList />
 * </Card>
 * ```
 *
 * @example Interativo (ghost)
 * ```tsx
 * <Card
 *   interactive
 *   onClick={openRegulation}
 *   ariaLabel="Abrir regulamento"
 *   variant="ghost"
 * >
 *   <CardHeader title="Regulamento" pill="Pendente" />
 *   <Description>Configure as regras do campeonato.</Description>
 * </Card>
 * ```
 */
function CardImpl(props: CardProps, ref: React.Ref<HTMLElement>) {
  const {
    children,
    variant = DEFAULT_VARIANT,
    padding = DEFAULT_PADDING,
    radius = DEFAULT_RADIUS,
    sx,
    className,
  } = props;

  // O type guard `props.interactive === true` reduz a union para
  // `InteractiveProps`, dando acesso seguro a onClick/ariaLabel/...
  if (props.interactive) {
    const showArrow = props.showArrow ?? true;
    return (
      <InteractiveRoot
        ref={ref as React.Ref<HTMLButtonElement>}
        $variant={variant}
        $padding={padding}
        $radius={radius}
        $interactive
        onClick={props.onClick}
        aria-label={props.ariaLabel}
        disabled={props.disabled}
        disableRipple
        className={className}
        sx={sx}
      >
        {children}
        {showArrow && !props.disabled && <Arrow />}
      </InteractiveRoot>
    );
  }

  return (
    <StaticRoot
      ref={ref as React.Ref<HTMLDivElement>}
      $variant={variant}
      $padding={padding}
      $radius={radius}
      $interactive={false}
      className={className}
      sx={sx}
    >
      {children}
    </StaticRoot>
  );
}

export const Card = memo(forwardRef(CardImpl));
Card.displayName = "Card";
